import CornerBg from "@/assets/img/illustrations/corner-2.png";
import Background from "@/components/atoms/Background";
import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import { formatInterlocutor } from "@/helpers/CompanyHelpers";
import { useClickToCall } from "@/hooks/useClickToCall";
import { CardDetailsProps } from "@/interfaces/Card";
import Company from "@/interfaces/Company";
import { useGetCompanyQuery } from "@/services/companies";
import { faEye, faPhone, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { FC, useEffect } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import CardHeader from "../../card-headers/CardHeader";

interface CompanyDetailsCardProps extends CardDetailsProps<Company> {
  showOrHiddenNoteModal?: (companyId: string) => void;
}

const CompanyDetailsCard: FC<CompanyDetailsCardProps> = ({
  id,
  card,
  showDetailsButton,
  callBack,
  showOrHiddenNoteModal,
}) => {
  const { data, isLoading, isError, error } = useGetCompanyQuery(id);
  const onCall = useClickToCall();

  useEffect(() => {
    data && callBack?.(data);
  }, [data]);

  const onTriggerModal = () => showOrHiddenNoteModal?.(id);

  const phoneNumber = data?.fixPhone ?? data?.phone;

  if (isLoading) {
    return <Loader absolute={!!card} />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute={!!card} returnButton />;
  }

  return (
    <div
      className={classNames({
        card: card,
      })}
    >
      {card && <CardHeader title="Informations de l'entreprise" />}
      <div
        className={classNames("bg-light", {
          "card-body": card,
        })}
      >
        <Card className="border rounded-3 bg-white dark__bg-1000 shadow-none">
          <Background
            image={CornerBg}
            className="bg-card d-none d-sm-block d-xl-none"
          />
          <Card.Body className="row g-0 flex-column flex-sm-row flex-xl-column z-index-1 align-items-center">
            <Col sm={8} md={6} lg={4} xl={12} className="ps-sm-1 ps-xl-0">
              <Flex alignItems="xl-center" direction="column">
                <p className="fw-semi-bold mb-0 text-800">{data?.name}</p>
                <Flex>
                  <SoftBadge
                    bg={data?.status?.backgroundColor ?? "danger"}
                    textColor={data?.status?.textColor}
                    className="my-2 text-xl-center"
                  >
                    {data?.status?.name ?? "Inconnu"}
                  </SoftBadge>
                  {data?.archived && (
                    <SoftBadge
                      bg={"danger"}
                      className="my-2 ms-2 text-xl-center"
                    >
                      Archiver
                    </SoftBadge>
                  )}
                </Flex>
              </Flex>
              <Flex justifyContent="center" className="gap-2">
                {showDetailsButton ? (
                  <IconButton
                    variant="primary"
                    icon={faEye}
                    iconAlign="left"
                    href={`/companies/${data!.id}`}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap flex-1"
                  >
                    <span className="fs--2">Détails</span>
                  </IconButton>
                ) : (
                  <IconButton
                    variant="primary"
                    icon={faPlus}
                    iconAlign="left"
                    onClick={onTriggerModal}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap flex-1"
                  >
                    <span className="fs--2">Note</span>
                  </IconButton>
                )}
                {phoneNumber && (
                  // <div> (pas <button>) pour pouvoir démarrer la sélection du
                  // texte directement dessus : un <button> intercepte le
                  // mousedown comme un appui. Stylé comme le bouton via les
                  // classes Bootstrap.
                  <div
                    className="btn btn-nolina-default btn-sm w-50 flex-1 d-flex align-items-center justify-content-center gap-2"
                    style={{ userSelect: "text" }}
                    onClick={() => {
                      // Ne pas appeler si l'utilisateur sélectionne le numéro.
                      if (window.getSelection()?.toString()) return;
                      onCall(phoneNumber!);
                    }}
                  >
                    <FontAwesomeIcon icon={faPhone} transform="shrink-4" />
                    <span className="fs--2" style={{ userSelect: "text" }}>
                      {phoneNumber}
                    </span>
                  </div>
                )}
              </Flex>
            </Col>
          </Card.Body>
        </Card>
        <Row className="border rounded-3 p-x1 mt-3 bg-white dark__bg-1000 mx-0 g-0">
          <Col md={6} xl={12} className="pe-md-4 pe-xl-0">
            <div className="mb-2">
              <Form.Label>Opco</Form.Label>
              <br />
              {data?.activitySector?.opco ? (
                <SoftBadge
                  bg={data?.activitySector?.opco?.backgroundColor ?? "danger"}
                  textColor={data?.activitySector?.opco?.textColor}
                >
                  {data?.activitySector?.opco?.name}
                </SoftBadge>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Est un renouvellement</Form.Label>
              <br />
              <SoftBadge bg={data?.renewal ? "success" : "danger"}>
                {data?.renewal ? "Oui" : "Non"}
              </SoftBadge>
            </div>
            <div className="mb-2">
              <Form.Label>Date de rappel</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.reminderDatetime ? (
                  DateTime.fromISO(data.reminderDatetime).toFormat(
                    "dd/MM/yyyy 'à' HH:mm"
                  )
                ) : (
                  <UnknownBadge />
                )}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Nom de l'entreprise</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.name ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Nom du gérant</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.owner?.fullname ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Interlocuteur</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {formatInterlocutor(
                  data?.interlocutorFirstname,
                  data?.interlocutorLastname
                ) ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Adresse</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.address ?? <UnknownBadge />}
              </p>
            </div>
            <Row className="gx-2">
              <Col xs={6} className="mb-2">
                <Form.Label>Code postal</Form.Label>
                <p className="mb-0 text-700 fs--1">
                  {data?.zipCode ?? <UnknownBadge />}
                </p>
              </Col>
              <Col xs={6} className="mb-2">
                <Form.Label>Ville</Form.Label>
                <p className="mb-0 text-700 fs--1">
                  {data?.city ?? <UnknownBadge />}
                </p>
              </Col>
            </Row>
            <div className="mb-2">
              <Form.Label>Téléphone Fixe</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.fixPhone ? (
                  <PhoneCallLink number={data.fixPhone} />
                ) : (
                  <UnknownBadge />
                )}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Téléphone portable</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.phone ? (
                  <PhoneCallLink number={data.phone} />
                ) : (
                  <UnknownBadge />
                )}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Téléphone du gérant</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.owner?.phone ? (
                  <PhoneCallLink number={data.owner.phone} />
                ) : (
                  <UnknownBadge />
                )}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Email</Form.Label>
              <br />
              {data?.email ? (
                <EmailLink email={data.email} />
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Siret</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.siret ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Lien société.com</Form.Label>
              <br />
              {data?.link ? (
                <Link
                  to={data.link}
                  className="mb-0 text-700 fs--1"
                  target="_blank"
                >
                  {data.link}
                </Link>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Secteur d'activité</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.activitySector?.name ?? <UnknownBadge />}
              </p>
            </div>
          </Col>
          <div className="mb-2">
            <Form.Label>Nombre de salarié prévu</Form.Label>
            <p className="mb-0 text-700 fs--1">
              {data?.defaultPeopleNumber ?? <UnknownBadge />}
            </p>
          </div>
          <div className="mb-2">
            <Form.Label>Nombre d'établissements prévus</Form.Label>
            <p className="mb-0 text-700 fs--1">
              {data?.defaultCompaniesNumber ?? <UnknownBadge />}
            </p>
          </div>
          <Col md={6} xl={12} className="ps-md-4 ps-xl-0">
            <div className="mb-2">
              <Form.Label>N° adhérent OPCO</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.opcoNumber ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>ID Opco</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.opcoIdentifier ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Mot de passe Opco</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.opcoPassword ?? <UnknownBadge />}
              </p>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CompanyDetailsCard;
